import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@ndd/ui";
import { resetMetricsQueries } from "@/lib/metrics-cache";
import { cn } from "@/lib/utils";
import type { RdHealthieLink } from "@/lib/use-rd-detail";
import { defaultVariantForTrack } from "../../../../shared/form-registry";
import { recentPastWeekStarts } from "../../../../shared/local-date";
import type { FormSnapshotData } from "../../../../shared/form-types";
import { BreadcrumbSetter } from "./breadcrumb";
import { RdHeaderStrip } from "./rd-header-strip";
import { FormsProfileCard, type FormProfileData } from "./forms-profile-card";
import { priorWeeklySnapshot } from "./form-snapshot-detail";
import { FormSnapshotList } from "./form-snapshot-list";
import { WeeklyForm } from "./weekly-form";
import { FormSnapshotFlyover } from "./form-snapshot-flyover";
import { initialFormsView, type FormsSurfaceView as View } from "./forms-initial-view";

/** How many past weeks the backfill picker reaches back. */
const BACKFILL_WEEKS = 12;

interface FormsSurfaceProps {
  rdId: string;
  /** Forwarded to the header strip; the route derives it from `canRenameRd`. */
  canRename: boolean;
  queryRdId: string;
  rdSlug: string;
  rdName: string;
  sessionCount: number;
  createdAt: string;
  rdSupervisorUserId: string | null;
  rdSupervisorName: string | null;
  healthieLink?: RdHealthieLink;
  profile: FormProfileData | null;
  snapshots: FormSnapshotData[];
  /** Deep-linked snapshot (`?edit=`) to open in the edit view on mount. */
  initialEditFormId?: string | null;
  /** `?profile=setup` from the Snapshot: open profile setup when none exists. */
  openProfileSetup?: boolean;
}

function useFormsView(initialView: View, initialEditFormId: string | null) {
  const [view, setView] = useState<View>(initialView);
  const [editingId, setEditingId] = useState<string | null>(initialEditFormId);
  const [flyoverFormId, setFlyoverFormId] = useState<string | null>(null);

  return { view, setView, editingId, setEditingId, flyoverFormId, setFlyoverFormId };
}

type FormsView = ReturnType<typeof useFormsView>;

function formSelection(
  profile: FormProfileData | null,
  snapshots: FormSnapshotData[],
  state: FormsView
) {
  const { view, editingId, flyoverFormId } = state;
  // No profile => no capture: the list's affordances are disabled and the
  // create/backfill views are unreachable, so there is no variant to guess.
  const defaultVariant = profile ? defaultVariantForTrack(profile.supportTrack) : null;
  const loggedWeeklyStarts = snapshots
    .filter((s) => s.formType === "weekly_form")
    .map((s) => s.periodStartDate);
  const backfillWeeks = recentPastWeekStarts(BACKFILL_WEEKS).filter(
    (week) => !loggedWeeklyStarts.includes(week)
  );
  const editingSnapshot =
    view === "edit"
      ? (snapshots.find((s) => s.id === editingId) ?? undefined)
      : undefined;
  // Edits carry their own variant, so they stay possible even for the data
  // edge of snapshots without a profile; fresh captures need the profile.
  const formVariantBasis = editingSnapshot?.formVariant ?? defaultVariant;
  const flyoverSnapshot = snapshots.find((s) => s.id === flyoverFormId) ?? null;

  return { backfillWeeks, editingSnapshot, formVariantBasis, flyoverSnapshot };
}

function formsActions(
  queryClient: ReturnType<typeof useQueryClient>,
  queryRdId: string,
  profile: FormProfileData | null,
  backfillWeeks: string[],
  state: FormsView
) {
  const { setView, setEditingId, setFlyoverFormId } = state;

  // Both writes land through the same /forms* routes; refresh the two queries
  // the /forms page loads (GET /api/rds/:id/forms and /forms/profile).
  function refreshForms() {
    void queryClient.invalidateQueries({ queryKey: ["forms", queryRdId] });
    void queryClient.invalidateQueries({ queryKey: ["forms-profile", queryRdId] });
  }

  const { openCreate, openBackfill } = captureActions(profile, backfillWeeks, state);

  function openEdit(formId: string) {
    setFlyoverFormId(null);
    setEditingId(formId);
    setView("edit");
  }

  function backToList() {
    setView("list");
    setEditingId(null);
  }

  function onWeeklySaved() {
    toast.success("Weekly form saved");
    backToList();
    refreshForms();
  }

  function onProfileSaved(savedProfile: FormProfileData) {
    toast.success("Profile updated");
    queryClient.setQueryData<{ data: FormProfileData | null }>(
      ["forms-profile", queryRdId],
      { data: savedProfile }
    );
    setView("list");
    refreshForms();
    // Billable hours regrades against the saved contract minimum on the next
    // metrics read, so no cached grade may outlive the save (NO-667).
    void resetMetricsQueries(queryClient);
  }

  return {
    openCreate,
    openBackfill,
    openEdit,
    backToList,
    onWeeklySaved,
    onProfileSaved,
  };
}

type FormsActions = ReturnType<typeof formsActions>;
type FormSelection = ReturnType<typeof formSelection>;

/**
 * The Functional Forms surface orchestrator (chats precedent — its own client
 * component, not an RdContentTabs tab). Owns the view state machine
 * (list · create · edit · profile-edit), the snapshot flyover selection, the
 * success toasts, and the discard guards inside the child forms. All writes are
 * fetch() to the /api/rds/[id]/forms* routes followed by a react-query
 * invalidation of the forms + profile lists.
 */
export function FormsSurface(props: FormsSurfaceProps) {
  const queryClient = useQueryClient();
  const initialEditFormId = props.initialEditFormId ?? null;
  const state = useFormsView(
    initialFormsView({
      initialEditFormId,
      openProfileSetup: props.openProfileSetup ?? false,
      hasProfile: props.profile !== null,
    }),
    initialEditFormId
  );
  const selection = formSelection(props.profile, props.snapshots, state);
  const actions = formsActions(
    queryClient,
    props.queryRdId,
    props.profile,
    selection.backfillWeeks,
    state
  );

  return (
    <FormsLayout props={props} state={state} selection={selection} actions={actions} />
  );
}

interface FormsContentProps {
  props: FormsSurfaceProps;
  state: FormsView;
  selection: FormSelection;
  actions: FormsActions;
}

function FormsLayout({ props, state, selection, actions }: FormsContentProps) {
  const {
    rdId,
    canRename,
    rdName,
    rdSlug,
    sessionCount,
    createdAt,
    rdSupervisorName,
    healthieLink,
    snapshots,
  } = props;
  const { flyoverFormId, setFlyoverFormId } = state;
  const { flyoverSnapshot } = selection;

  return (
    <div className="flex h-full min-h-0">
      <div className="min-w-0 flex-1 overflow-y-auto">
        <div
          className={cn(
            "mx-auto flex flex-col gap-4 px-6 py-6",
            flyoverFormId ? "max-w-[640px]" : "max-w-[920px]"
          )}
        >
          <BreadcrumbSetter
            segments={[
              { label: "RDs", href: "/rds" },
              { label: rdName, href: `/rds/${rdSlug}` },
              { label: "Functional Forms" },
            ]}
          />
          <RdHeaderStrip
            rdId={rdId}
            canEdit={canRename}
            initialName={rdName}
            sessionCount={sessionCount}
            createdAt={createdAt}
            supervisorName={rdSupervisorName}
            healthieLink={healthieLink}
          />

          <FormsContent
            props={props}
            state={state}
            selection={selection}
            actions={actions}
          />
        </div>
      </div>

      <FormSnapshotFlyover
        rdSlug={rdSlug}
        snapshot={flyoverSnapshot}
        prior={priorWeeklySnapshot(snapshots, flyoverSnapshot)}
        onClose={() => setFlyoverFormId(null)}
        onEdit={actions.openEdit}
      />
    </div>
  );
}

function FormsContent({ props, state, selection, actions }: FormsContentProps) {
  const { rdId, rdName, rdSupervisorUserId, rdSupervisorName, profile, snapshots } =
    props;
  const { view, setView, setFlyoverFormId } = state;
  const { backfillWeeks, editingSnapshot, formVariantBasis } = selection;
  const { backToList, onProfileSaved, onWeeklySaved, openCreate, openBackfill } =
    actions;
  const formOpen = view === "create" || view === "backfill" || view === "edit";

  return (
    <>
      <FormsProfileCard
        key={view === "profile-edit" ? (profile?.updatedAt ?? "new") : "view"}
        rdId={rdId}
        profile={profile}
        editing={view === "profile-edit"}
        canEdit={view === "list"}
        onEditStart={() => setView("profile-edit")}
        onCancel={backToList}
        onSaved={onProfileSaved}
      />

      {formOpen && formVariantBasis ? (
        <WeeklyForm
          rdId={rdId}
          rdName={rdName}
          rdSupervisorUserId={rdSupervisorUserId}
          rdSupervisorName={rdSupervisorName}
          defaultVariant={formVariantBasis}
          backfillWeeks={view === "backfill" ? backfillWeeks : undefined}
          snapshot={editingSnapshot}
          onSuccess={onWeeklySaved}
          onClose={backToList}
        />
      ) : (
        <FormSnapshotList
          snapshots={snapshots}
          canCapture={profile !== null}
          onAdd={openCreate}
          onBackfill={openBackfill}
          onOpen={setFlyoverFormId}
        />
      )}
    </>
  );
}

function captureActions(
  profile: FormProfileData | null,
  backfillWeeks: string[],
  state: FormsView
) {
  const { setView, setEditingId, setFlyoverFormId } = state;

  function openCreate() {
    if (!profile) {
      toast.error("Set up the profile to enable weekly forms.");

      return;
    }

    setFlyoverFormId(null);
    setEditingId(null);
    setView("create");
  }

  function openBackfill() {
    if (!profile) {
      toast.error("Set up the profile to enable weekly forms.");

      return;
    }

    if (backfillWeeks.length === 0) {
      toast.error(`The last ${BACKFILL_WEEKS} weeks already have weekly forms.`);

      return;
    }

    setFlyoverFormId(null);
    setEditingId(null);
    setView("backfill");
  }

  return { openCreate, openBackfill };
}
